import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

const CONDITIONS = [
  { value:'new',       label:'New',       desc:'Brand new, unused' },
  { value:'like_new',  label:'Like New',  desc:'Used once or twice' },
  { value:'excellent', label:'Excellent', desc:'Near-perfect condition' },
  { value:'very_good', label:'Very Good', desc:'Minor wear, fully functional' },
  { value:'good',      label:'Good',      desc:'Noticeable wear, works perfectly' },
  { value:'fair',      label:'Fair',      desc:'Heavy use, fully functional' },
];

export default function EditListingPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [error,     setError]     = useState('');
  const [categories,setCategories]= useState([]);
  const [images,    setImages]    = useState([]);
  const [form, setForm] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      API.get(`/listings/${id}`),
      API.get('/categories'),
    ]).then(([listingRes, catsRes]) => {
      const l = listingRes.data.listing;
      if (l.seller_id !== user.id && user.role !== 'admin') {
        navigate(`/listing/${id}`);
        return;
      }
      setForm({
        categoryId:     l.category_id || '',
        title:          l.title || '',
        description:    l.description || '',
        condition:      l.condition || '',
        brand:          l.brand || '',
        model:          l.model || '',
        year:           l.year || '',
        listingType:    l.listing_type || 'sale',
        price:          l.price || '',
        weeklyRate:     l.weekly_rate || '',
        depositAmount:  l.deposit_amount || '',
        minRentalDays:  l.min_rental_days || 1,
        maxRentalDays:  l.max_rental_days || 30,
        startingBid:    l.starting_bid || '',
        reservePrice:   l.reserve_price || '',
        auctionDays:    l.auction_ends_at ? Math.max(1, Math.ceil((new Date(l.auction_ends_at) - new Date()) / 86400000)) : 7,
        locationCity:   l.location_city || '',
        locationState:  l.location_state || '',
        locationZip:    l.location_zip || '',
        shippingAvailable: l.shipping_available || false,
        localPickupOnly:   l.local_pickup_only !== false,
        freeShipping:      l.free_shipping || false,
        instantBooking:    l.instant_booking !== false,
        idRequired:        l.id_required || false,
        listingCouponCode: l.listing_coupon_code || '',
        listingCouponType: l.listing_coupon_type || 'fixed',
        listingCouponAmount: l.listing_coupon_amount || '',
      });
      setImages(l.images || []);
      setCategories(catsRes.data.categories);
      setLoading(false);
    }).catch(() => { navigate('/dashboard/listings'); });
  }, [id, user]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const onDrop = useCallback(async (files) => {
    for (const file of files.slice(0, 10 - images.length)) {
      const fd = new FormData();
      fd.append('image', file);
      try {
        const { data } = await API.post('/upload/listing-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setImages(prev => [...prev, { ...data, preview: URL.createObjectURL(file) }]);
      } catch {}
    }
  }, [images]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'image/*': [] }, maxSize: 10 * 1024 * 1024
  });

  const removeImage = (imgId) => setImages(prev => prev.filter(i => i.id !== imgId));

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required'); return;
    }
    setSaving(true); setError('');
    try {
      await API.put(`/listings/${id}`, {
        ...form,
        price:         form.price        ? parseFloat(form.price)        : undefined,
        startingBid:   form.startingBid  ? parseFloat(form.startingBid)  : undefined,
        reservePrice:  form.reservePrice ? parseFloat(form.reservePrice) : undefined,
        depositAmount: form.depositAmount? parseFloat(form.depositAmount): undefined,
        weeklyRate:    form.weeklyRate   ? parseFloat(form.weeklyRate)   : undefined,
        year:          form.year         ? parseInt(form.year)           : undefined,
        listingCouponAmount: form.listingCouponCode ? parseFloat(form.listingCouponAmount)||0 : null,
        auctionEndsAt: form.listingType === 'auction'
          ? new Date(Date.now() + form.auctionDays * 86400000).toISOString()
          : undefined,
      });
      setSaved(true);
      setTimeout(() => navigate(`/listing/${id}`), 1500);
    } catch (err) {
      console.error('Save error:', err.response?.data || err.message);
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading || !form) return (
    <div style={{ textAlign:'center', padding:'80px' }}>
      <div style={{ fontSize:40 }} className="float-anim">🍒</div>
    </div>
  );

  return (
    <div style={{ maxWidth:680, margin:'0 auto', padding:'40px 24px 80px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:32 }}>
        <button onClick={() => navigate(`/listing/${id}`)}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:14, color:'var(--cherry)', fontWeight:600 }}>
          ← Back to listing
        </button>
      </div>

      <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, marginBottom:6 }}>Edit Listing</h1>
      <p style={{ fontSize:14, color:'var(--text-soft)', marginBottom:28 }}>Changes save immediately and go live on your listing.</p>

      {error  && <div className="alert alert-error"   style={{ marginBottom:16 }}>{error}</div>}
      {saved  && <div className="alert alert-success" style={{ marginBottom:16 }}>✅ Saved! Redirecting...</div>}

      {/* ── Listing Type ── */}
      <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, marginBottom:20 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, marginBottom:16 }}>Listing Type</div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {[
            { value:'sale',    icon:'🟢', label:'Sell it',     desc:'Fixed price sale' },
            { value:'rent',    icon:'🔵', label:'Rent it out', desc:'Earn by renting by the day' },
            { value:'auction', icon:'🔴', label:'Auction it',  desc:'Let bidders compete' },
          ].map(t => (
            <div key={t.value} onClick={() => f('listingType', t.value)}
              style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 16px', borderRadius:12, border:`2px solid ${form.listingType===t.value?'var(--cherry)':'var(--border)'}`, background:form.listingType===t.value?'var(--cherry-light)':'white', cursor:'pointer', transition:'all .2s' }}>
              <span style={{ fontSize:24 }}>{t.icon}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>{t.label}</div>
                <div style={{ fontSize:12, color:'var(--text-soft)' }}>{t.desc}</div>
              </div>
              <div style={{ marginLeft:'auto', width:18, height:18, borderRadius:'50%', border:`2px solid ${form.listingType===t.value?'var(--cherry)':'var(--border)'}`, background:form.listingType===t.value?'var(--cherry)':'white' }} />
            </div>
          ))}
        </div>
      </div>

      {/* ── Details ── */}
      <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, marginBottom:20 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, marginBottom:16 }}>Details</div>

        <div className="form-group">
          <label className="form-label">Category</label>
          <select className="form-select" value={form.categoryId} onChange={e => f('categoryId', e.target.value)}>
            <option value="">Select category</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Title <span className="required">*</span></label>
          <input className="form-input" value={form.title} onChange={e => f('title', e.target.value)} maxLength={200} />
        </div>

        <div className="form-group">
          <label className="form-label">Description <span className="required">*</span></label>
          <textarea className="form-textarea" value={form.description} onChange={e => f('description', e.target.value)} style={{ minHeight:100 }} />
        </div>

        <div className="form-group">
          <label className="form-label">Condition</label>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {CONDITIONS.map(c => (
              <div key={c.value} onClick={() => f('condition', c.value)}
                style={{ padding:'10px 12px', borderRadius:9, border:`1.5px solid ${form.condition===c.value?'var(--cherry)':'var(--border)'}`, background:form.condition===c.value?'var(--cherry-light)':'white', cursor:'pointer', transition:'all .2s' }}>
                <div style={{ fontWeight:700, fontSize:13 }}>{c.label}</div>
                <div style={{ fontSize:11, color:'var(--text-soft)' }}>{c.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Brand</label>
            <input className="form-input" placeholder="e.g. Sony" value={form.brand} onChange={e => f('brand', e.target.value)} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Model</label>
            <input className="form-input" placeholder="e.g. A7 IV" value={form.model} onChange={e => f('model', e.target.value)} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Year</label>
            <input className="form-input" type="number" placeholder="e.g. 2022"
              min="1900" max={new Date().getFullYear()}
              value={form.year} onChange={e => f('year', e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Pricing ── */}
      <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, marginBottom:20 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, marginBottom:16 }}>Pricing</div>

        {form.listingType === 'sale' && (
          <div className="form-group">
            <label className="form-label">Sale Price</label>
            <div className="price-input-wrap">
              <span className="price-prefix">$</span>
              <input className="price-field" type="number" step="0.01" min="0"
                value={form.price} onChange={e => f('price', e.target.value)} />
            </div>
            {form.price && <div className="form-hint">You'll receive ${(form.price * 0.9).toFixed(2)} after the 10% platform fee</div>}
          </div>
        )}

        {form.listingType === 'rent' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Daily Rate</label>
              <div className="price-input-wrap">
                <span className="price-prefix">$</span>
                <input className="price-field" type="number" step="0.01" min="0"
                  value={form.price} onChange={e => f('price', e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Security Deposit</label>
              <div className="price-input-wrap">
                <span className="price-prefix">$</span>
                <input className="price-field" type="number" step="0.01" min="0"
                  value={form.depositAmount} onChange={e => f('depositAmount', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {form.listingType === 'auction' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Starting Bid</label>
              <div className="price-input-wrap">
                <span className="price-prefix">$</span>
                <input className="price-field" type="number" step="0.01" min="0"
                  value={form.startingBid} onChange={e => f('startingBid', e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Reserve Price</label>
              <div className="price-input-wrap">
                <span className="price-prefix">$</span>
                <input className="price-field" type="number" step="0.01" min="0"
                  value={form.reservePrice} onChange={e => f('reservePrice', e.target.value)} />
              </div>
              <div className="form-hint">Optional hidden minimum</div>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Auction Duration</label>
              <select className="form-select" value={form.auctionDays} onChange={e => f('auctionDays', parseFloat(e.target.value))}>
                <option value="0.125">⚡ Lightning (3 hours)</option>
                <option value="1">1 day</option>
                <option value="3">3 days</option>
                <option value="5">5 days</option>
                <option value="7">7 days</option>
                <option value="10">10 days</option>
                <option value="14">14 days</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* ── Coupon ── */}
      <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, marginBottom:20 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, marginBottom:16 }}>🎟️ Listing Coupon (Optional)</div>
        <p style={{ fontSize:13, color:'var(--text-soft)', marginBottom:16 }}>Add a discount code that works only for this listing</p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Coupon Code</label>
            <input className="form-input" placeholder="e.g. SAVE10" value={form.listingCouponCode} onChange={e => f('listingCouponCode', e.target.value.toUpperCase())} maxLength="50" />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Type</label>
            <select className="form-select" value={form.listingCouponType} onChange={e => f('listingCouponType', e.target.value)}>
              <option value="fixed">Fixed ($)</option>
              <option value="percentage">Percentage (%)</option>
            </select>
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Amount</label>
            <input className="form-input" type="number" placeholder="0.00" value={form.listingCouponAmount} onChange={e => f('listingCouponAmount', e.target.value)} step="0.01" min="0" />
          </div>
        </div>
        {form.listingCouponCode && (
          <div style={{ marginTop:12, padding:'10px 12px', background:'#F3E5F5', borderRadius:8, fontSize:12, color:'#6A1B9A', fontWeight:600 }}>
            ✓ Coupon "{form.listingCouponCode}" will be available at checkout
          </div>
        )}
      </div>

      {/* ── Photos ── */}
      <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, marginBottom:20 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, marginBottom:16 }}>Photos</div>

        <div {...getRootProps()} style={{ border:`2px dashed ${isDragActive?'var(--cherry)':'var(--border)'}`, borderRadius:12, padding:28, textAlign:'center', cursor:'pointer', background:isDragActive?'var(--cherry-light)':'var(--cream)', transition:'all .2s', marginBottom:16 }}>
          <input {...getInputProps()} />
          <div style={{ fontSize:28, marginBottom:8 }}>📸</div>
          <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>Click to add photos or drag & drop</div>
          <div style={{ fontSize:12, color:'var(--text-soft)' }}>JPG, PNG, WebP up to 10MB · up to 10 photos</div>
        </div>

        {images.length > 0 && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
            {images.map((img, i) => (
              <div key={img.id || i} style={{ position:'relative', aspectRatio:'1', borderRadius:10, overflow:'hidden', border:`2px solid ${i===0?'var(--cherry)':'var(--border)'}` }}>
                <img src={img.preview || `${API_BASE}${img.url}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                {i===0 && <div style={{ position:'absolute',bottom:0,left:0,right:0,background:'var(--cherry)',color:'white',fontSize:9,fontWeight:700,textAlign:'center',padding:2 }}>COVER</div>}
                <button onClick={() => removeImage(img.id)} style={{ position:'absolute',top:4,right:4,width:20,height:20,borderRadius:'50%',background:'rgba(0,0,0,.7)',border:'none',color:'white',cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center' }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Location ── */}
      <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, marginBottom:20 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, marginBottom:16 }}>Location</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">City</label>
            <input className="form-input" value={form.locationCity} onChange={e => f('locationCity', e.target.value)} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">State</label>
            <input className="form-input" value={form.locationState} onChange={e => f('locationState', e.target.value)} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">ZIP</label>
            <input className="form-input" value={form.locationZip} onChange={e => f('locationZip', e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Options ── */}
      <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:24, marginBottom:28 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, marginBottom:16 }}>Options</div>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {[
            { key:'localPickupOnly',   label:'Local pick up',          desc:'No shipping, meet in person' },
            { key:'shippingAvailable', label:'Offer shipping',         desc:'Ship via integrated carriers' },
            { key:'freeShipping',      label:'Free shipping',          desc:'You cover the shipping cost' },
            { key:'instantBooking',    label:'Instant booking',        desc:'Auto-confirm without approval' },
            { key:'idRequired',        label:'Require ID verification',desc:'Only verified users can book' },
          ].map(o => (
            <label key={o.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
              <div>
                <div style={{ fontWeight:600, fontSize:14 }}>{o.label}</div>
                <div style={{ fontSize:12, color:'var(--text-soft)' }}>{o.desc}</div>
              </div>
              <label className="toggle-wrap">
                <input type="checkbox" checked={form[o.key]} onChange={e => f(o.key, e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </label>
          ))}
        </div>
      </div>

      {/* ── Save buttons ── */}
      <div style={{ display:'flex', gap:12 }}>
        <button className="btn btn-cherry btn-lg" onClick={handleSave} disabled={saving} style={{ flex:1 }}>
          {saving ? <><span className="spinner" /> Saving...</> : '💾 Save Changes'}
        </button>
        <button className="btn btn-ghost" onClick={() => navigate(`/listing/${id}`)}>Cancel</button>
      </div>
    </div>
  );
}
